import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const StyledCityCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  font-size: var(--t-sm);
  color: var(--ink);
`;

export const StyledCityDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--brand-accent, var(--info));
  flex-shrink: 0;
`;

export const StyledForm = styled.form`
  width: 100%;
  max-width: 26rem;
  z-index: 10;
`;

export const StyledTableWrapper = styled.div`
  width: 100%;
  overflow: clip;
`;

export const StyledFailedHero = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: var(--s-4) var(--s-5);
  margin-bottom: 20px;
  border-radius: var(--r-lg);
  background: var(--danger-soft);
  border: 1px solid rgba(212, 121, 116, 0.2);
`;

export const StyledFailedHeroBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StyledFailedHeroTitle = styled.h2`
  margin: 0;
  font-size: var(--t-lg);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
  color: var(--danger-ink);
`;

export const StyledFailedHeroSub = styled.p`
  margin: 0;
  font-size: var(--t-sm);
  color: var(--ink-2);
  max-width: 44rem;
`;

export const StyledFailedHeroCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 99px;
  background: rgba(212, 121, 116, 0.14);
  border: 1px solid rgba(212, 121, 116, 0.25);
  font-size: var(--t-sm);
  font-weight: var(--weight-bold);
  color: var(--danger-ink);
  margin-right: 6px;
`;

export const StyledValueCell = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  color: var(--ink);
`;

export const StyledBreakdownSection = styled.div`
  margin-bottom: 20px;
  padding: var(--s-4) var(--s-5);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
`;

export const StyledBreakdownHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
`;

export const StyledBreakdownTitle = styled.h3`
  margin: 0;
  font-size: var(--t-base);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  letter-spacing: -0.005em;
`;

export const StyledBreakdownHint = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
`;

export const StyledBreakdownSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledBreakdownSkeletonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 2fr auto auto auto;
  align-items: center;
  gap: 14px;
  padding: 6px 12px;
`;

export const StyledSkeletonPill = styled.div<{ width?: string }>`
  height: 12px;
  width: ${({ width }) => width ?? '100%'};
  border-radius: 99px;
  background: linear-gradient(
    90deg,
    rgba(9,15,56,0.06) 25%,
    rgba(9,15,56,0.11) 50%,
    rgba(9,15,56,0.06) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export const StyledBreakdownRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledShowLessButton = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: var(--t-xs);
  color: var(--muted);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover { color: var(--ink-2); }
`;

export const StyledBreakdownRow = styled.button<{ active: boolean }>`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 2fr auto auto auto;
  align-items: center;
  gap: 14px;
  padding: 6px 12px;
  border-radius: var(--r-md);
  border: 0;
  background: ${({ active }) => active ? 'var(--danger-soft)' : 'transparent'};
  cursor: pointer;
  text-align: left;
  font: inherit;
  transition: background var(--dur-fast) var(--ease-out);

  &:hover {
    background: ${({ active }) => active ? 'var(--danger-soft)' : 'rgba(9,15,56,0.03)'};
  }
`;

export const StyledBreakdownReason = styled.span`
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledBreakdownBarTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: var(--paper-2);
  overflow: hidden;
`;

export const StyledBreakdownBarFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  border-radius: 4px;
  background: linear-gradient(90deg, #FF9D9A, var(--danger));
  transition: width var(--dur-slow) var(--ease-out);
`;

export const StyledBreakdownCount = styled.span`
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  min-width: 32px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

export const StyledBreakdownPct = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
  min-width: 36px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

export const StyledActiveReasonPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px 0 12px;
  border-radius: var(--r-sm);
  border: 1px solid rgba(212, 121, 116, 0.35);
  background: var(--danger-soft);
  color: var(--danger-ink);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  cursor: pointer;
  max-width: 256px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background var(--dur-fast) var(--ease-out);

  &:hover { background: rgba(212, 121, 116, 0.12); }

  svg { flex-shrink: 0; width: 14px; height: 14px; }
`;

export const StyledReasonTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: var(--s-1) var(--s-2);
  border-radius: 99px;
  background: var(--danger-soft);
  border: 1px solid rgba(212, 121, 116, 0.2);
  color: var(--danger-ink);
  font-size: var(--t-xs);
  font-weight: var(--weight-medium);
  white-space: nowrap;
  max-width: 224px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledTrendBadge = styled.span<{ trend: 'up' | 'down' | 'stable' }>`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: var(--weight-bold);
  color: ${({ trend }) =>
    trend === 'up' ? 'var(--danger)' :
    trend === 'down' ? 'var(--brand-green)' :
    'transparent'};
  margin-left: 4px;
  user-select: none;
`;

export const StyledInfoTooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  cursor: default;

  svg {
    width: 14px;
    height: 14px;
    color: var(--muted-2);
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    flex-shrink: 0;
  }

  &:hover::before,
  &:hover::after { opacity: 1; pointer-events: auto; }

  &::before {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    width: 240px;
    padding: var(--s-2) var(--s-2);
    background: var(--brand-ink);
    color: var(--surface);
    font-size: var(--t-xs);
    font-weight: 400;
    line-height: 1.45;
    border-radius: var(--r-sm);
    white-space: normal;
    text-align: left;
    pointer-events: none;
    opacity: 0;
    transition: opacity var(--dur-fast) var(--ease-out);
    z-index: 100;
    box-shadow: var(--sh-3);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: var(--brand-ink);
    pointer-events: none;
    opacity: 0;
    transition: opacity var(--dur-fast) var(--ease-out);
    z-index: 100;
  }
`;

export const StyledWhatsAppButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--line-2);
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out);
  color: var(--brand-green);

  &:hover {
    background: rgba(37, 211, 102, 0.08);
    border-color: rgba(37, 211, 102, 0.35);
  }

  svg { width: 16px; height: 16px; }
`;

export const StyledBulkBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: var(--s-3) var(--s-5);
  background: var(--surface);
  border: 1px solid var(--line-2);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-4);
  z-index: 200;
  white-space: nowrap;
`;

export const StyledBulkBarLabel = styled.span`
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  color: var(--ink);
`;

export const StyledBulkCancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  background: var(--danger);
  border: none;
  border-radius: var(--r-sm);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  color: var(--surface);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out);

  &:hover:not(:disabled) { background: var(--danger-ink); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ── Stat grid (3-col hero row) ─────────────────────────────────── */
export const StyledStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: var(--s-4);
  margin-bottom: var(--s-4);
`;

export const StyledStatLabel = styled.div`
  font-size: var(--t-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: var(--s-2);
`;

export const StyledStatValue = styled.div<{ $danger: boolean }>`
  display: flex;
  align-items: baseline;
  gap: var(--s-1);
  font-size: var(--t-2xl);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: ${({ $danger }) => $danger ? 'var(--danger-ink)' : 'var(--ink)'};
  margin-bottom: var(--s-1);
`;

export const StyledStatUnit = styled.span`
  font-size: var(--t-sm);
  color: var(--muted);
  font-weight: var(--weight-regular);
`;

export const StyledStatSub = styled.div`
  font-size: var(--t-xs);
  color: var(--muted);
  margin-top: var(--s-1);
`;

export const StyledCityBadgesRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--s-2);
  margin-top: var(--s-3);
  flex-wrap: wrap;
  row-gap: var(--s-2);
`;

export const StyledCityBadgePill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--s-1);
  padding: var(--s-1) var(--s-2);
  border-radius: var(--r-full);
  background: ${({ $active }) => $active ? 'var(--danger)' : 'var(--danger-soft)'};
  color: ${({ $active }) => $active ? 'var(--surface)' : 'var(--danger-ink)'};
  border: 0;
  font: inherit;
  font-size: var(--t-xs);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);

  &:hover {
    background: ${({ $active }) => $active ? 'var(--danger-ink)' : 'rgba(212, 121, 116, 0.18)'};
  }
`;

/* ── Breakdown reason block (Arabic + English label) ────────────── */
export const StyledBreakdownReasonBlock = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const StyledBreakdownReasonAr = styled.span<{ $active?: boolean }>`
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  color: ${({ $active }) => $active ? 'var(--danger-ink)' : 'var(--ink)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledBreakdownReasonEn = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ── Re-place button link ────────────────────────────────────────── */
export const StyledReplaceLinkButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 var(--s-3);
  border-radius: var(--r-sm);
  background: var(--brand-ink);
  color: var(--surface);
  font: inherit;
  font-size: var(--t-xs);
  font-weight: var(--weight-semibold);
  text-decoration: none;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);

  &:hover { background: var(--ink); color: var(--surface); }
`;
